import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Footer() {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = (formData.get('company_alt') as string || '').trim();
    if (honeypot) {
      setFormStatus('success');
      setEmail('');
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key !== 'company_alt') {
        payload.append(key, value as string);
      }
    });

    try {
      const response = await fetch('https://readdy.ai/api/form/d958v5dmi650so75e95g', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });

      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam = typeof serverMsg === 'string' && (serverMsg.includes('spam') || serverMsg.includes('form data is spam'));

      if (response.ok && parsed?.code === 'OK' && !isSpam) {
        setFormStatus('success');
        setEmail('');
        form.reset();
      } else {
        setFormStatus('error');
        setFormError(typeof serverMsg === 'string' ? serverMsg : 'Ошибка отправки. Попробуйте позже.');
      }
    } catch {
      setFormStatus('error');
      setFormError('Сетевой сбой. Пожалуйста, проверьте подключение и попробуйте снова.');
    }
  };

  return (
    <footer className="bg-background-900 text-background-50">
      <div className="px-6 md:px-10 max-w-6xl mx-auto py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-10">
          <div>
            <Link to="/" className="font-heading text-xl font-bold text-white tracking-tight whitespace-nowrap">
              YOYBUY<span className="text-primary-300">.boats</span>
            </Link>
            <p className="text-foreground-300 text-xs mt-4 leading-relaxed">
              {t('footer_desc_line1')}<br />
              {t('footer_desc_line2')}<br />
              {t('footer_desc_line3')}<br />
              {t('footer_desc_line4')}
            </p>
          </div>

          <div>
            <h4 className="text-xs tracking-widest uppercase text-foreground-400 font-label mb-5">
              {t('footer_subscribe_title')}
            </h4>
            <form onSubmit={handleSubmit} data-readdy-form="newsletter" className="relative">
              <input
                type="text"
                name="company_alt"
                className="honeypot-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />
              <input
                type="email"
                name="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('footer_subscribe_placeholder')}
                required
                className="w-full bg-transparent border-b border-foreground-500/40 py-2 pr-10 text-sm text-white placeholder:text-foreground-400 focus:outline-none focus:border-primary-400 transition-colors"
              />
              <button type="submit" className="absolute right-0 top-1/2 -translate-y-1/2 text-foreground-400 hover:text-primary-300 transition-colors cursor-pointer">
                <i className="ri-arrow-right-line text-lg"></i>
              </button>
            </form>
            {formStatus === 'success' && (
              <p className="text-primary-300 text-xs mt-3">Спасибо! Вы подписаны.</p>
            )}
            {formStatus === 'error' && (
              <p className="text-accent-400 text-xs mt-3">{formError}</p>
            )}
            {formStatus === 'idle' && (
              <p className="text-foreground-500 text-xs mt-3">
                {t('footer_subscribe_hint')}
                <Link to="/" className="text-primary-300 hover:text-primary-200 transition-colors underline">
                  {t('footer_subscribe_hint_link')}
                </Link>
              </p>
            )}
          </div>

          <div>
            <h4 className="text-xs tracking-widest uppercase text-foreground-400 font-label mb-5">
              {t('footer_categories_title')}
            </h4>
            <div className="flex flex-col gap-2">
              {categories.slice(0, 6).map((cat) => (
                <a
                  key={cat.key}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="text-sm text-foreground-300 hover:text-primary-300 transition-colors whitespace-nowrap"
                >
                  {cat.name}
                </a>
              ))}
            </div>
          </div>

          <div>
            <h4 className="text-xs tracking-widest uppercase text-foreground-400 font-label mb-5">
              {t('footer_contacts_title')}
            </h4>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block text-sm text-foreground-300 hover:text-primary-300 transition-colors mb-2 whitespace-nowrap"
            >
              kaspreadsheet.com
            </a>
            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="block text-sm text-foreground-300 hover:text-primary-300 transition-colors mb-2 whitespace-nowrap"
            >
              YOYBUY Sheet
            </a>
            <Link
              to="/yoybuy-sheet-guide"
              className="block text-sm text-foreground-300 hover:text-primary-300 transition-colors whitespace-nowrap"
            >
              {t('nav_articles')}
            </Link>
          </div>
        </div>
      </div>

      <div className="border-t border-foreground-700/30">
        <div className="px-6 md:px-10 max-w-6xl mx-auto py-8">
          <div className="text-7xl md:text-9xl font-heading font-black text-background-800 tracking-tighter text-center select-none">
            {t('footer_brand')}
          </div>
          <p className="text-center text-foreground-500 text-xs mt-4">
            &copy; {new Date().getFullYear()} YOYBUY.boats. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}